<template>
    <div>
        <base-spinner v-if="isLoading"/>
        <base-page v-else class="add-surgery-page">
            <b-row class="justify-content-center">
                <b-col cols="9" class="bg-white">
                    <b-row class="justify-content-end mr-3 my-3">
                        <b-link @click="$bvModal.show('cancel-dialog')">{{ $t('utils.cancel') }} </b-link>
                    </b-row>

                    <div class="mb-3">
                        <h2 class="text-center font-weight-bold title-2">
                            {{ surgeryDescription.surgery_type | surgeryType | t }}
                        </h2>
                        <h5 v-if="surgeryDescription.description && surgeryDescription.surgery_type !== 'tooth_extraction'"
                            class="text-center title">
                            {{ surgeryDescription.description }}</h5>
                    </div>

                    <component
                        class="mx-3"
                        :is="componentList[current]"
                        ref="child"
                        :surgeryType="surgeryDescription.surgery_type"
                        @description="surgeryDescription=$event"
                    />

                    <general-error :error="error"/>

                    <step-navigation
                        :steps="componentList.length"
                        :should-go-next="next"
                        :should-go-back="back"
                        :save="save"
                        @current="componentChanged"
                    />
                </b-col>
            </b-row>

            <exit-confirm-dialog
                :back-route="{ name: 'patient-detail-surgeries', params: { patientId: patientId } }"
                modal-id="cancel-dialog"
            />
        </base-page>
    </div>
</template>

<script>
    import TypeSelection from "@/components/surgery/create/TypeSelection";
    import BasalStudy from "@/components/surgery/create/BasalStudy";
    import Transfusion from "@/components/surgery/create/Transfusion";
    import AdjuvantTreatment from "@/components/surgery/create/AdjuvantTreatment";
    import SurgeryData from "@/components/surgery/create/SurgeryData";
    import SurgeryComplications from "@/components/surgery/create/SurgeryComplications";
    import {mapActions, mapState} from "vuex";
    import CreateEmptyObject from "@/components/mixins/CreateEmptyObject";
    import {MAJOR_SURGERY, MINOR_SURGERY, TOOTH_EXTRACTION} from "@/components/surgery/SurgeryUtils";
    import {mapStateProperty} from "@/utils/VuexHelpers";
    import StepNavigation from "@/components/StepNavigation";
    import GeneralError from "@/components/GeneralError";
    import ExitConfirmDialog from "@/components/ExitConfirmDialog";
    import BasePage from "@/components/BasePage";
    import BasePageMixin from "@/components/BasePageMixin";
    import BaseSpinner from "@/components/BaseSpinner";
    import {logRequestError} from "@/utils/utils";

    export default {
        name: "SurgeryAddPage",
        components: {
            BaseSpinner,
            BasePage,
            ExitConfirmDialog,
            GeneralError,
            StepNavigation,
        },
        filters: {
            surgeryType: function (value) {
                switch (value) {
                    case MAJOR_SURGERY:
                        return 'surgery.majorSurgery';
                    case MINOR_SURGERY:
                        return 'surgery.minorSurgery';
                    case TOOTH_EXTRACTION:
                        return 'surgery.addSimpleToothExtraction';
                    default:
                        return 'surgery.addSurgery';
                }
            }
        },
        mixins: [CreateEmptyObject, BasePageMixin],
        props: {
            patientId: {
                type: Number,
                required: true,
            },
        },
        data() {
            return {
                componentList: [
                    TypeSelection,
                    BasalStudy,
                    Transfusion,
                    AdjuvantTreatment,
                    SurgeryData,
                    SurgeryComplications,
                ],
                current: 0,
                surgeryDescription: {
                    description: '',
                    surgery_type: this.$t('surgery.addSurgery'),
                },
                error: false,
            };
        },
        computed: {
            ...mapState([
                'surgery',
                'surgeryFields',
                'bloodTestFields',
                'transfusionFields',
                'novoeightTreatmentFields',
                'adjuvantTreatmentFields',
                'surgeryDescriptions',
            ]),
            ...mapStateProperty([
                'surgery.patient',
            ]),
        },
        async created() {
            this.setSurgery(this.createEmptyObjectFromFields(this.surgeryFields));
            await this.getSurgeryOptions();
            await this.getBloodTestOptions();
            await this.getTransfusionOptions();
            await this.getNovoeightTreatmentOptions();
            await this.getAdjuvantTreatmentOptions();
            await this.getSurgeryDescriptions();
            this.isLoading = false;
        },
        destroyed() {
            this.setSurgery(null);
        },
        methods: {
            ...mapActions([
                'setSurgery',
                'createSurgery',
                'getSurgeryOptions',
                'getSurgeryDescriptions',
                'getBloodTestOptions',
                'getTransfusionOptions',
                'getNovoeightTreatmentOptions',
                'getAdjuvantTreatmentOptions',
            ]),
            async next() {
                return await this.$refs.child.validate();
            },
            async back() {
                await this.$refs.child.back();
                return true;
            },
            async save() {
                this.error = false;
                if (await this.$refs.child.validate()) {
                    this.patient = this.patientId;
                    this.createSurgery(this.surgery)
                        .then(surgery => {
                            this.$router.replace({ name: 'surgery-detail', params: {
                                    surgeryId: surgery.id,
                                }});
                        })
                        .catch(error => {
                            logRequestError(error);
                            this.error = true;
                        });
                }
            },
            componentChanged(index) {
                this.current = index;
            }
        },
    };
</script>

<style scoped="">
</style>